// เจาะรายคน (ติดตามนักเรียนภาคศาสนา)
// รวมทุกสัญญาณของนักเรียนหนึ่งคนไว้หน้าเดียว โดยอ่านจากโมเดลรายด้านที่มีอยู่เดิม
// ตัวเลขจึงตรงกับหน้าต้นทางของแต่ละเรื่อง (เช็คชื่อศาสนา / ฝ่ายปกครอง / น้ำหนักส่วนสูง)
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Badge, Button, Card, Col, Row, Table } from 'react-bootstrap'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { useLanguage } from '../contexts/LanguageContext'
import { StudentName, studentDisplayName } from '../utils/studentName'
import th from '../locales/islamic/th'
import jawi from '../locales/islamic/jawi'
import {
  StudentAvatar, LevelBadge, bmiOf, fmtDate, useDarkTables, alertRequestError,
  useIslamicTrackingRole,
} from './trackingShared'

const API = process.env.REACT_APP_API
// รายการเช็คชื่อทั้งปีมีได้หลายร้อยแถว — โชว์ล่าสุดเท่านี้ แล้วบอกยอดรวมไว้
const ATT_DETAIL_LIMIT = 50

const ATT_META = {
  present: { bg: 'success', key: 'statusPresent' },
  absent:  { bg: 'danger',  key: 'statusAbsent' },
  late:    { bg: 'warning', key: 'statusLate' },
  leave:   { bg: 'info',    key: 'statusLeave' },
}
const CONCERN_STATUS = {
  open:        { bg: 'danger',  key: 'concernOpen' },
  in_progress: { bg: 'warning', key: 'concernProgress' },
  resolved:    { bg: 'success', key: 'concernResolved' },
}
const CONCERN_CAT = {
  learning: 'catLearning', health: 'catHealth', family: 'catFamily',
  emotional: 'catEmotional', peer: 'catPeer', other: 'catOther',
}
const MERIT_STATUS = {
  assigned:  { bg: 'secondary', key: 'meritAssigned' },
  completed: { bg: 'success',   key: 'meritCompleted' },
  cancelled: { bg: 'light',     key: 'meritCancelled' },
}

const Stat = ({ label, value, tone }) => (
  <Col xs={4} md={2}>
    <div className="border rounded text-center py-2 h-100">
      <div className={`fs-4 fw-semibold lh-1${tone ? ` text-${tone}` : ''}`}>{value}</div>
      <div className="fs-xs text-secondary mt-1">{label}</div>
    </div>
  </Col>
)

// การ์ดหนึ่งหัวข้อ — ว่างเมื่อไหร่ยังแสดงหัวข้อกับข้อความ "ยังไม่มีข้อมูล" ไว้
// เพื่อให้ครูรู้ว่าเรื่องนี้มีในระบบแต่ยังไม่มีข้อมูล ไม่ใช่หายไปเพราะบั๊ก
const Section = ({ title, icon, note, empty, isEmpty, children, extra }) => (
  <Card className="card-one mb-3">
    <Card.Header className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
      <Card.Title as="h6" className="mb-0"><i className={`${icon} me-2 text-primary`} />{title}</Card.Title>
      {extra}
    </Card.Header>
    <Card.Body className={isEmpty ? undefined : 'p-0'}>
      {note && <div className="fs-xs text-secondary px-3 pt-2">{note}</div>}
      {isEmpty ? <div className="text-center text-secondary py-3">{empty}</div> : children}
    </Card.Body>
  </Card>
)

export default function IslamicStudentTrackingDetail() {
  const { studentID } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { setLoading } = useLoading()
  const { lang } = useLanguage()
  const t = lang === 'th' ? th : jawi
  const td = t.trackingDetail
  const { roleLabel } = useIslamicTrackingRole(t)
  useDarkTables()

  const yearId = searchParams.get('yearId') || localStorage.getItem('yearId')
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)

  const load = useCallback(() => {
    if (!studentID || !yearId) return
    setLoading(true)
    axiosInstance.get(`${API}/islamic/tracking/student/${studentID}/${yearId}`)
      .then(res => { setData(res.data); setError(null) })
      .catch(err => {
        setData(null)
        setError(err?.response?.status === 403 ? td.noAccess : td.notFound)
        if (err?.response?.status === 503) alertRequestError(t, err, t.careTracking.loadError)
      })
      .finally(() => setLoading(false))
  }, [studentID, yearId, setLoading, t, td])

  useEffect(() => { load() }, [load])

  const student = data?.student
  const att = useMemo(() => {
    const rows = data?.subjectAttendance || []
    const sum = rows.reduce((acc, r) => ({
      present: acc.present + Number(r.present_count || 0),
      absent:  acc.absent + Number(r.absent_count || 0),
      late:    acc.late + Number(r.late_count || 0),
      leave:   acc.leave + Number(r.leave_count || 0),
      total:   acc.total + Number(r.total || 0),
    }), { present: 0, absent: 0, late: 0, leave: 0, total: 0 })
    return { ...sum, rate: sum.total > 0 ? +((sum.absent / sum.total) * 100).toFixed(1) : 0 }
  }, [data])

  const attDetail = data?.attendanceDetail || []
  const shownDetail = attDetail.slice(0, ATT_DETAIL_LIMIT)
  const conduct = data?.conduct?.summary
  const healthNotes = useMemo(() => {
    if (!student) return []
    return [
      [td.healthDrug, student.st_drug],
      [td.healthFood, student.st_food],
      [td.healthSyndrome, student.st_syndrome],
      [td.healthEver, student.st_everSyndrome],
      [td.healthFacing, student.st_facingSyndrome],
      [td.healthAccident, student.st_accidentHistory],
    ].filter(([, v]) => String(v || '').trim())
  }, [student, td])

  if (error) {
    return (
      <React.Fragment>
        <Button variant="outline-secondary" size="sm" className="mb-3" onClick={() => navigate(-1)}>
          <i className="ri-arrow-left-line me-1" />{td.back}
        </Button>
        <Card className="card-one"><Card.Body className="text-center text-secondary py-5">{error}</Card.Body></Card>
      </React.Fragment>
    )
  }
  if (!student) return null

  return (
    <React.Fragment>
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-4">
        <div className="d-flex align-items-center gap-3">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
            <i className="ri-arrow-left-line" />
          </Button>
          <StudentAvatar photo={student.st_photo} name={studentDisplayName(student, lang)} size={56} />
          <div>
            <div className="fs-sm text-secondary mb-1">{roleLabel} · {td.breadcrumb}</div>
            <h4 className="main-title mb-0"><StudentName student={student} lang={lang} /></h4>
            <div className="fs-sm text-secondary">
              {student.st_idnumber}
              {student.class_room && <> · {td.infoRoom} {student.class_room}</>}
              {student.is_programCode && student.is_programCode !== 'GP' && <> ({student.is_programCode})</>}
              {student.regular_room && <> · {td.infoRegular} {student.regular_room}</>}
            </div>
          </div>
        </div>
      </div>

      {/* สรุปการเข้าเรียนภาคศาสนา */}
      <Card className="card-one mb-3">
        <Card.Header>
          <Card.Title as="h6" className="mb-0"><i className="ri-calendar-check-line me-2 text-primary" />{td.sumAttendance}</Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-2">
            <Stat label={td.sumPresent} value={att.present} tone="success" />
            <Stat label={td.sumAbsent} value={att.absent} tone="danger" />
            <Stat label={td.sumLate} value={att.late} tone="warning" />
            <Stat label={td.sumLeave} value={att.leave} tone="info" />
            <Stat label={td.sumTotal} value={att.total} />
            <Stat label={td.sumRate} value={`${att.rate}%`} tone={att.rate >= 20 ? 'danger' : att.rate >= 10 ? 'warning' : undefined} />
          </Row>
        </Card.Body>
      </Card>

      {/* การเข้าเรียนแยกตามรายวิชา */}
      <Section
        title={td.bySubject} icon="ri-book-2-line"
        empty={td.noSubjectAtt} isEmpty={!(data.subjectAttendance || []).length}
      >
        <Table responsive hover size="sm" className="mb-0 align-middle">
          <thead className="table-secondary">
            <tr>
              <th style={{ minWidth: 160 }}>{td.colSubject}</th>
              <th style={{ minWidth: 120 }}>{td.colTeacher}</th>
              <th className="text-center">{td.sumPresent}</th>
              <th className="text-center">{td.sumAbsent}</th>
              <th className="text-center">{td.sumLate}</th>
              <th className="text-center">{td.sumLeave}</th>
              <th className="text-center">{td.sumTotal}</th>
            </tr>
          </thead>
          <tbody>
            {(data.subjectAttendance || []).map((r, i) => (
              <tr key={`${r.sub_id ?? 'x'}-${i}`}>
                <td>
                  <div className="fw-semibold arabic-text">{r.sub_subname || '-'}</div>
                  {r.sub_subid && <div className="fs-xs text-secondary">{r.sub_subid}</div>}
                </td>
                <td className="fs-sm">{r.t_name || '-'}</td>
                <td className="text-center">{r.present_count || 0}</td>
                <td className="text-center fw-semibold text-danger">{r.absent_count || 0}</td>
                <td className="text-center">{r.late_count || 0}</td>
                <td className="text-center">{r.leave_count || 0}</td>
                <td className="text-center text-secondary">{r.total || 0}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>

      {/* รายการเช็คชื่อรายคาบ */}
      <Section
        title={td.attDetail} icon="ri-list-check-2"
        empty={td.noAttDetail} isEmpty={!attDetail.length}
        extra={attDetail.length > ATT_DETAIL_LIMIT && (
          <span className="fs-xs text-secondary">{td.attDetailMore(shownDetail.length, attDetail.length)}</span>
        )}
      >
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          <Table responsive hover size="sm" className="mb-0 align-middle">
            <thead className="table-secondary sticky-top">
              <tr>
                <th style={{ minWidth: 100 }}>{td.colDate}</th>
                <th className="text-center" style={{ width: 60 }}>{td.colPeriod}</th>
                <th style={{ minWidth: 140 }}>{td.colSubject}</th>
                <th className="text-center" style={{ width: 90 }}>{td.colStatus}</th>
                <th style={{ minWidth: 140 }}>{td.colTopic}</th>
              </tr>
            </thead>
            <tbody>
              {shownDetail.map((r, i) => {
                const meta = ATT_META[r.att_status] || ATT_META.present
                return (
                  <tr key={`${r.att_date}-${r.period_id}-${i}`}>
                    <td className="fs-sm">{fmtDate(r.att_date)}</td>
                    <td className="text-center fs-sm">{r.period_order ?? '-'}</td>
                    <td className="fs-sm arabic-text">{r.sub_subname || '-'}</td>
                    <td className="text-center">
                      <Badge bg={meta.bg} text={meta.bg === 'warning' ? 'dark' : undefined} className="fs-xs">{td[meta.key]}</Badge>
                    </td>
                    <td className="fs-xs text-secondary">{r.tr_topic || '-'}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      </Section>

      {/* ความประพฤติ */}
      <Card className="card-one mb-3">
        <Card.Header>
          <Card.Title as="h6" className="mb-0"><i className="ri-shield-user-line me-2 text-primary" />{td.conduct}</Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-2 mb-3">
            <Stat label={td.conductRemaining} value={conduct?.remaining_points ?? 100}
              tone={(conduct?.remaining_points ?? 100) < 70 ? 'danger' : (conduct?.remaining_points ?? 100) < 90 ? 'warning' : 'success'} />
            <Stat label={td.conductDeducted} value={conduct?.deducted_points ?? 0} />
            <Stat label={td.conductMerit} value={conduct?.merit_points ?? 0} />
          </Row>
          {(data.conduct?.records || []).length === 0 ? (
            <div className="text-center text-secondary py-2">{td.noConduct}</div>
          ) : (
            <Table responsive hover size="sm" className="mb-0 align-middle">
              <thead className="table-secondary">
                <tr>
                  <th style={{ minWidth: 100 }}>{td.colDate}</th>
                  <th style={{ minWidth: 160 }}>{td.colRule}</th>
                  <th className="text-center" style={{ width: 80 }}>{td.colPoints}</th>
                  <th style={{ minWidth: 120 }}>{td.colBy}</th>
                </tr>
              </thead>
              <tbody>
                {(data.conduct.records || []).map(r => (
                  <tr key={r.record_id}>
                    <td className="fs-sm">{fmtDate(r.created_at)}</td>
                    <td className="fs-sm">
                      {r.rule_name_snapshot}
                      {r.note && <div className="fs-xs text-secondary">{r.note}</div>}
                    </td>
                    <td className="text-center"><Badge bg="danger" className="fs-xs">-{r.deduct_points}</Badge></td>
                    <td className="fs-sm">{r.created_by_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* งานจิตอาสา */}
      <Section title={td.merits} icon="ri-hand-heart-line" empty={td.noMerits} isEmpty={!(data.conduct?.merits || []).length}>
        <Table responsive hover size="sm" className="mb-0 align-middle">
          <thead className="table-secondary">
            <tr>
              <th style={{ minWidth: 100 }}>{td.colDate}</th>
              <th style={{ minWidth: 160 }}>{td.colActivity}</th>
              <th className="text-center" style={{ width: 80 }}>{td.colPoints}</th>
              <th className="text-center" style={{ width: 110 }}>{td.colStatus}</th>
            </tr>
          </thead>
          <tbody>
            {(data.conduct?.merits || []).map(m => {
              const meta = MERIT_STATUS[m.status] || MERIT_STATUS.assigned
              return (
                <tr key={m.merit_id}>
                  <td className="fs-sm">{fmtDate(m.activity_date || m.created_at)}</td>
                  <td className="fs-sm">
                    {m.activity_name_snapshot}
                    {m.note && <div className="fs-xs text-secondary">{m.note}</div>}
                  </td>
                  <td className="text-center"><Badge bg="success" className="fs-xs">+{m.add_points}</Badge></td>
                  <td className="text-center">
                    <Badge bg={meta.bg} text={meta.bg === 'light' ? 'dark' : undefined} className="fs-xs">{td[meta.key]}</Badge>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Section>

      {/* เรื่องที่แจ้งดูแล */}
      <Section title={td.concerns} icon="ri-flag-2-line" note={td.concernsNote}
        empty={td.noConcerns} isEmpty={!(data.concerns || []).length}>
        <Table responsive hover size="sm" className="mb-0 align-middle">
          <thead className="table-secondary">
            <tr>
              <th style={{ minWidth: 100 }}>{td.colDate}</th>
              <th style={{ minWidth: 120 }}>{td.colCategory}</th>
              <th style={{ minWidth: 200 }}>{td.colDetail}</th>
              <th className="text-center" style={{ width: 100 }}>{td.colStatus}</th>
              <th style={{ minWidth: 120 }}>{td.colBy}</th>
            </tr>
          </thead>
          <tbody>
            {(data.concerns || []).map(c => {
              const meta = CONCERN_STATUS[c.concern_status] || CONCERN_STATUS.open
              return (
                <tr key={c.concern_id}>
                  <td className="fs-sm">{fmtDate(c.created_at)}</td>
                  <td className="fs-sm">
                    {td[CONCERN_CAT[c.concern_category]] || c.concern_category}
                    {c.sub_subname && <div className="fs-xs text-secondary">{c.sub_subname}</div>}
                  </td>
                  <td className="fs-sm">{c.concern_detail}</td>
                  <td className="text-center">
                    <Badge bg={meta.bg} text={meta.bg === 'warning' ? 'dark' : undefined} className="fs-xs">{td[meta.key]}</Badge>
                  </td>
                  <td className="fs-sm">{c.created_by_name || '-'}</td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Section>

      {/* คะแนนรายวิชาศาสนา (ข้อมูลประกอบ ไม่ใช่แกนความเสี่ยง) */}
      <Section title={td.scores} icon="ri-grid-line" note={td.scoresNote}
        empty={td.noScores} isEmpty={!(data.scores || []).length}>
        <Table responsive hover size="sm" className="mb-0 align-middle">
          <thead className="table-secondary">
            <tr>
              <th style={{ minWidth: 160 }}>{td.colSubject}</th>
              <th style={{ minWidth: 120 }}>{td.colTeacher}</th>
              <th className="text-center" style={{ width: 80 }}>{td.colTerm1}</th>
              <th className="text-center" style={{ width: 80 }}>{td.colTerm2}</th>
              <th className="text-center" style={{ width: 80 }}>{td.colScoreAvg}</th>
            </tr>
          </thead>
          <tbody>
            {(data.scores || []).map((s, i) => (
              <tr key={`${s.sub_subid ?? 'x'}-${i}`}>
                <td>
                  <div className="fw-semibold arabic-text">{s.sub_subname || '-'}</div>
                  <div className="fs-xs text-secondary">{[s.sub_subid, s.isubject_isid2].filter(Boolean).join(' / ')}</div>
                </td>
                <td className="fs-sm">{s.t_name || '-'}</td>
                <td className="text-center">{s.is_scoreVal1 ?? <span className="text-secondary">-</span>}</td>
                <td className="text-center">{s.is_scoreVal2 ?? <span className="text-secondary">-</span>}</td>
                <td className="text-center fw-semibold">{s.is_scoreVal ?? <span className="text-secondary fw-normal">-</span>}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>

      {/* น้ำหนัก/ส่วนสูง + ข้อมูลสุขภาพ */}
      <Section title={td.health} icon="ri-heart-pulse-line" empty={td.noHealth}
        isEmpty={!(data.bodyMass || []).length && healthNotes.length === 0}>
        {(data.bodyMass || []).length > 0 && (
          <Table responsive hover size="sm" className="mb-0 align-middle">
            <thead className="table-secondary">
              <tr>
                <th style={{ minWidth: 120 }}>{td.colMonth}</th>
                <th className="text-center">{td.colHeight}</th>
                <th className="text-center">{td.colWeight}</th>
                <th className="text-center">{td.colBmi}</th>
              </tr>
            </thead>
            <tbody>
              {[...(data.bodyMass || [])].sort((a, b) => (a.stmb_month || 0) - (b.stmb_month || 0)).map(b => {
                const bmi = bmiOf(b.stbm_height, b.stbm_weight)
                const out = bmi != null && (bmi < 18.5 || bmi > 25)
                return (
                  <tr key={b.stbm_id}>
                    <td className="fs-sm">{t.months[(Number(b.stmb_month) || 1) - 1] || b.stmb_month}</td>
                    <td className="text-center">{b.stbm_height ?? '-'}</td>
                    <td className="text-center">{b.stbm_weight ?? '-'}</td>
                    <td className="text-center">
                      {bmi == null ? '-' : <LevelBadge level={out ? 'yellow' : 'green'}>{bmi}</LevelBadge>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
        {healthNotes.length > 0 && (
          <div className="p-3 border-top">
            <div className="fw-semibold fs-sm mb-2">{td.healthNotes}</div>
            <Row className="g-2">
              {healthNotes.map(([label, value]) => (
                <Col key={label} xs={12} md={6}>
                  <div className="border rounded p-2 h-100">
                    <div className="fs-xs text-secondary">{label}</div>
                    <div className="fs-sm">{value}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Section>
    </React.Fragment>
  )
}
